import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudUpload } from "@fortawesome/free-solid-svg-icons/faCloudUpload";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Badge } from "react-bootstrap";
import { TrackFileMetaData } from "../../../../api/useMusicApi";
import { faFileEdit } from "@fortawesome/free-solid-svg-icons/faFileEdit";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ReactPopover } from "./ReactPopover";

type Props = {
  isNew?: boolean;
  fileMeta?: TrackFileMetaData;
  progress: number | null;
  file?: File | null;
  onDelete: () => void;
  onReloadFile: () => void;
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
  else if (bytes < 1073741824) return (bytes / 1048576).toFixed(2) + " MB";
  else return (bytes / 1073741824).toFixed(2) + " GB";
}

const secondsToStringDuration = (totalSecs: number) => {
  let minutes: number | string = Math.floor(totalSecs / 60);
  let seconds: number | string = totalSecs % 60;
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }

  if (seconds < 10) {
    seconds = `0${seconds}`;
  }

  return `${minutes}:${seconds}`;
};

export const TrackUploadBar: React.FC<Props> = ({
                                                  file,
                                                  isNew,
                                                  fileMeta,
                                                  progress,
                                                  onDelete,
                                                  onReloadFile
                                                }) => {
  const { t } = useTranslation();
  let minContainerWidth = progress == null || progress === 100 ? 120 : 500;
  if (fileMeta?.duration == null) {
    minContainerWidth = minContainerWidth - 50;
  }

  return (
    <div
      style={{ minWidth: minContainerWidth }}
      className={"flex items-center gap-2 justify-content-end"}
    >
      {file != null && progress != null && progress !== 100 && (
        <div className={"flex items-center gap-2"} style={{ width: 200 }}>
          <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }} />
          </div>
          {progress}%
        </div>
      )}

      {file == null && fileMeta != null && (
        <div className={"text-right text-muted"}>
          {secondsToStringDuration(fileMeta.duration)}
        </div>
      )}

      {isNew && (
        <div>
          <Badge bg={"success"}>new</Badge>
        </div>
      )}

      {(progress === 100 || file == null) && (
        <ReactPopover
          trigger={"hover"}
          className={"flex items-center"}
          content={
            <div className={"flex flex-col gap-2 text-sm"}>
              <div className={"w-full text-muted flex justify-between gap-1 h-full items-center"}>
                <div className={"text-sm"}>{fileMeta?.name || file?.name}</div>
                <div>
                  <FontAwesomeIcon
                    icon={faFileEdit}
                    title={t("reload_file").toString()}
                    className={"cursor-pointer"}
                    onClick={onReloadFile}
                  />
                </div>
              </div>
              <div>
                {file != null && (
                  <div className={"text-muted"}>
                    {formatFileSize(file?.size ?? 0)}
                  </div>
                )}

                {file == null && fileMeta != null && (
                  <div className={"text-muted"}>
                    {fileMeta.size} Mb
                  </div>
                )}
              </div>
            </div>
          }
        >
          <FontAwesomeIcon
            icon={faCloudUpload}
            className={"text-success cursor-pointer"}
            size={"xl"}
          />
        </ReactPopover>
      )}
      {(progress === 100 || file == null) && (
        <FontAwesomeIcon
          icon={faTrash}
          size={"xl"}
          className={"cursor-pointer text-muted hover:text-red-500"}
          onClick={onDelete}
        />
      )}
    </div>
  );
};

